import CustomImage from 'components/UI/image/CustomImage';
import { DocumentIcon } from 'components/import-total-cell/ImportTotalCell';
import { errorLabel } from 'components/import-total-cell/importTotals';
import useGetUnreadNotificationCount from 'hooks/query/messages/useGetUnreadNotificationCount';
import useMarkNotificationRead from 'hooks/query/messages/useMarkNotificationRead';
import useGetNotificationFeed from 'hooks/query/notifications/useGetNotificationFeed';
import useEvents from 'hooks/store/events/useEvents';
import useSocketStore from 'hooks/store/messages/useSocketStore';
import useNotificationsStore from 'hooks/store/notifications/useNotificationsStore';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UTCToFormattedPartnerTimeZone,
  UTCToPartnerTimeZone,
  cookieDecryption,
  generateCustomAvatar,
  getDurationDate,
} from 'utils';
import { unread_notify } from 'utils/images';
import { isValidURL } from 'utils/urlValidator';
import { formatNotificationFeed, NotificationFeedRow } from './notificationFeedItem';

/**
 * IMP-2139 — the bell dropdown, reading the Notification Feed v2 REST endpoint.
 *
 * Previously this rendered `notificationResponse` from the socket store, which
 * served the legacy Notification collection, with one hardcoded branch per
 * legacy notification type. It now reads useNotificationsStore, which the feed
 * request writes into.
 *
 * Row shape, copy and destinations live in notificationFeedItem.ts; this file is
 * layout, pagination and navigation only.
 */

/** Pixels from the bottom of the tray at which the next page is requested. */
const SCROLL_BOTTOM_THRESHOLD = 24;

interface NotificationProps {
  /**
   * Closes the tray. The header owns that state, and the tray no longer lets
   * a click reach the header's own toggle, so navigating away from a row has
   * to say so explicitly.
   */
  onClose?: () => void;
}

const Notification = ({ onClose }: NotificationProps) => {
  const navigate = useNavigate();
  const user = cookieDecryption('user');

  const { setState: setEventsState } = useEvents();
  const { setState: setSocketState } = useSocketStore();
  const { markNotificationAsRead, markAllNotificationsAsRead } = useMarkNotificationRead();
  const { getUnreadNotificationCount } = useGetUnreadNotificationCount();
  const { fetchNotificationFeed, refreshNotificationFeed } = useGetNotificationFeed();
  const unreadNotificationCount = useSocketStore((state: any) => state.getNotificationCount);

  // Field by field rather than the whole store, so a pagination write does not
  // re-render more than it has to.
  const feed = useNotificationsStore(state => state.feed);
  const page = useNotificationsStore(state => state.page);
  const pages = useNotificationsStore(state => state.pages);
  const isLoading = useNotificationsStore(state => state.isLoading);
  const isLoadingMore = useNotificationsStore(state => state.isLoadingMore);
  const isRefreshing = useNotificationsStore(state => state.isRefreshing);
  const isError = useNotificationsStore(state => state.isError);
  const errorIsLoadMore = useNotificationsStore(state => state.errorIsLoadMore);
  const ownerId = useNotificationsStore(state => state.ownerId);
  const hasLoaded = useNotificationsStore(state => state.hasLoaded);
  const markReadLocally = useNotificationsStore(state => state.markReadLocally);
  const markAllReadLocally = useNotificationsStore(state => state.markAllReadLocally);
  const openFeedFor = useNotificationsStore(state => state.openFeedFor);
  const closeFeed = useNotificationsStore(state => state.closeFeed);

  /**
   * The header mounts this tray only while it is open, so the page behind it
   * stays locked for exactly as long as the component is alive. This used to
   * key off an isOpen prop that the header never passed, which meant the
   * effect always read the tray as closed and the page never stopped
   * scrolling.
   */
  useEffect(() => {
    document.body.style.overflowY = 'hidden';

    return () => {
      document.body.style.overflowY = 'visible';
    };
  }, []);

  /**
   * openFeedFor runs first so the fetch below captures the requestId that
   * belongs to this open. Any cache from a previous open by the same admin
   * survives and renders straight away, because renderBody checks for rows
   * ahead of isLoading, so the refetch swaps in fresh rows without a spinner.
   */
  useEffect(() => {
    openFeedFor(user?.userId);
    fetchNotificationFeed({ page: 1 });

    return () => {
      closeFeed();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * A rise in the unread count means a notification arrived while the tray was
   * open, so the list is refreshed in place rather than left stale until the
   * next open. Only a rise: this session also receives its own 'getCounts'
   * push after marking a row read, and refreshUnreadNotificationCount writes
   * the count locally, both of which move it down. Triggering on any change
   * would refetch on the reader's own actions.
   */
  const lastUnreadCount = useRef(unreadNotificationCount);
  const [isRefreshQueued, setIsRefreshQueued] = useState(false);

  useEffect(() => {
    if (unreadNotificationCount > lastUnreadCount.current) {
      setIsRefreshQueued(true);
    }

    lastUnreadCount.current = unreadNotificationCount;
  }, [unreadNotificationCount]);

  /**
   * Only one feed request may be in flight. Firing a refresh alongside a fetch
   * meant the older response could land last and undo the newer one, so the
   * refresh waits here until the tray is idle and then runs on its own.
   */
  useEffect(() => {
    if (!isRefreshQueued || isLoading || isLoadingMore || isRefreshing) {
      return;
    }

    setIsRefreshQueued(false);
    refreshNotificationFeed();
  }, [isRefreshQueued, isLoading, isLoadingMore, isRefreshing, refreshNotificationFeed]);

  /**
   * The badge cannot be derived from this list: marking an already-read
   * notification is a no-op, and the bell counts categories the feed excludes,
   * so only the backend can say what the new count is. The 'getCounts' socket
   * push (IMP-717) still applies the same update for any other open session;
   * this just stops this session's badge from waiting on it.
   */
  const refreshUnreadNotificationCount = () => {
    getUnreadNotificationCount().then(count => {
      if (typeof count === 'number') {
        setSocketState({ getNotificationCount: count });
      }
    });
  };

  /**
   * openFeedFor wipes a cache belonging to another admin, but it runs in an
   * effect and effects run after the first paint, so on its own it would let
   * one frame of the previous admin's notifications reach the screen. Deciding
   * ownership during render instead removes the timing question entirely:
   * nothing derived from a feed this admin does not own is renderable.
   */
  const ownsFeed = ownerId === user?.userId;

  const rows = useMemo(
    () => (ownsFeed ? formatNotificationFeed(feed, { role: user?.role }) : []),
    [feed, user?.role, ownsFeed]
  );
  const hasUnread = rows.some(row => !row.isRead);
  const feedHasLoaded = ownsFeed && hasLoaded;
  const feedHasError = ownsFeed && isError;

  const handleMarkAllAsRead = async () => {
    if (!hasUnread) {
      return;
    }

    if (await markAllNotificationsAsRead()) {
      markAllReadLocally();
      refreshUnreadNotificationCount();
    }
  };

  /**
   * The read call is deliberately not awaited before navigating. Waiting would
   * hold the dropdown open on a slow network for no benefit, since the row is
   * about to be replaced by the destination page anyway.
   */
  const markRowAsRead = (row: NotificationFeedRow) => {
    if (row.isRead) {
      return;
    }

    markNotificationAsRead(row.id).then(success => {
      if (success) {
        markReadLocally(row.id);
        refreshUnreadNotificationCount();
      }
    });
  };

  /**
   * Navigation does not depend on the read call. `markRowAsRead` returns early
   * for a row that is already read, but that is its own guard -- opening a row
   * you have seen before still takes you where it points, which is the whole
   * reason the destination is on the row rather than on the unread state.
   */
  const openRow = (row: NotificationFeedRow) => {
    markRowAsRead(row);

    if (row.routeState) {
      setEventsState(row.routeState);
    }

    // An import whose payload carries no targetUserRole has nowhere to go.
    // Guessing a participants list would send a mentor import to the mentee
    // one, so the row marks itself read and stays put.
    if (!row.route) {
      return;
    }

    navigate(row.route);
    onClose?.();
  };

  /**
   * The error report for a finished import. Downloading is the only action an
   * import row offers, so it marks the row read on the way out -- otherwise
   * the badge would keep counting a notification the admin has plainly acted
   * on.
   *
   * The tray deliberately stays open: unlike every other row this does not
   * navigate, and closing the panel out from under a download would look like
   * the click went somewhere.
   */
  const downloadImportReport = (row: NotificationFeedRow) => {
    markRowAsRead(row);

    const reportUrl = row.importReport?.reportUrl;

    // Same guard the Import History cell applies to the same URL. It arrives
    // signed from the backend, but it is still a URL from a response being
    // handed to window.open.
    if (reportUrl && isValidURL(reportUrl)) {
      window.open(reportUrl, '_blank', 'noopener,noreferrer');

      return;
    }

    console.warn('[Notification] Blocked unsafe import report URL:', reportUrl);
  };

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (isLoading || isLoadingMore || isRefreshing || page >= pages) {
        return;
      }

      const list = event.currentTarget;

      if (list.scrollHeight - list.scrollTop - list.clientHeight <= SCROLL_BOTTOM_THRESHOLD) {
        fetchNotificationFeed({ page: page + 1, isLoadMore: true });
      }
    },
    [fetchNotificationFeed, isLoading, isLoadingMore, isRefreshing, page, pages]
  );

  /**
   * Retries whichever request failed. With rows on screen the failure was a
   * load-more, so the next page is re-requested; with none it was page 1.
   */
  /**
   * Retries whatever actually failed. Rows on screen no longer imply a
   * load-more failure, because a cached list survives the tray closing, so a
   * failed page 1 has to be retried as page 1 rather than as the next page.
   */
  const handleRetry = () => {
    if (errorIsLoadMore) {
      fetchNotificationFeed({ page: page + 1, isLoadMore: true });

      return;
    }

    fetchNotificationFeed({ page: 1 });
  };

  /**
   * The feed projection carries no sender timezone (`sender.partnerAdmin` and
   * `sender.region` are not projected), so timestamps render against the
   * viewer's own timezone. The legacy row preferred the sender's.
   */
  const renderTimestamp = (createdAt?: string) => {
    const timezone = user?.timezone;
    const now = UTCToPartnerTimeZone(new Date()?.toISOString(), timezone);
    const sent = UTCToPartnerTimeZone(createdAt, timezone);
    const duration = moment?.duration(now?.diff(sent));

    return (
      <div className="bottom_sec d_flex">
        <span className="time">{getDurationDate(duration, UTCToFormattedPartnerTimeZone(createdAt, timezone))} </span>
        <span className="date">
          {sent.format('MMM DD, yyyy')} {` (${timezone?.abbr})`}
        </span>
      </div>
    );
  };

  /**
   * The "22 errors" chip on an import row, and the only thing on that row a
   * click can reach.
   *
   * Nothing is rendered for a clean import: there is no report to fetch, and a
   * dead "0 errors" would invite a click that cannot do anything. The
   * unclickable variant covers a settled import whose report failed to upload
   * -- the count is still true and worth showing, it just has nothing behind
   * it.
   */
  const renderImportErrors = (row: NotificationFeedRow) => {
    const report = row.importReport;

    if (!report || report.errorCount === 0) {
      return null;
    }

    const label = errorLabel(report.errorCount);

    if (!report.reportUrl) {
      return <span className="notification_import_errors_plain">{label}</span>;
    }

    return (
      <button
        type="button"
        className="notification_import_errors"
        aria-label={`Download error report for ${row.title}`}
        onClick={() => downloadImportReport(row)}>
        <DocumentIcon />
        <span>{label}</span>
      </button>
    );
  };

  /**
   * An import has no person behind it, so the sender avatar every other row
   * shows would be the reader's own face reporting their own upload. The
   * design puts the action there instead: an upload glyph, badged with how it
   * went, which is the only per-row iconography in this tray.
   *
   * Left out of the accessibility tree on purpose. The badge repeats what the
   * headline already says in words ("... uploaded with 22 errors"), so
   * labelling it would announce the outcome twice. `data-state` carries the
   * variant for tests, which otherwise have nothing but a class name to
   * assert on.
   */
  const renderImportIcon = (row: NotificationFeedRow) => (
    <div
      className={`notify_pic notification_import_icon ${row.importReport?.state ?? 'ok'}`}
      data-testid="import-outcome-icon"
      data-state={row.importReport?.state ?? 'ok'}>
      <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
        <path
          d="M12 16V5m0 0L7.5 9.5M12 5l4.5 4.5M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );

  const renderRowContent = (row: NotificationFeedRow) =>
    row.importReport ? (
      <>
        {renderImportIcon(row)}
        <div className="notification_inner d_flex flex_direction_column">
          {/* The stored sentence is the headline. The counts line below it is
              composed from the payload, because no single string can carry a
              clickable fragment. */}
          {/* A file name has no spaces to wrap at, so a long one ran past the
              tray and pushed a horizontal scrollbar into it. Cut to one line
              in CSS; the whole sentence stays reachable on hover. */}
          <p className="user_project notification_import_title" title={row.title}>
            {row.title}
          </p>
          <p className="notification_import_summary">
            <span>{row.body}</span>
            {renderImportErrors(row)}
          </p>
          {renderTimestamp(row.createdAt)}
          <div className="unread_notify d_flex_center">
            <CustomImage src={unread_notify} />
          </div>
        </div>
      </>
    ) : (
      <>
        <div className="notify_pic">
          <CustomImage
            src={
              row.avatarUrl ||
              generateCustomAvatar(
                `${row.avatarFirstName} ${row.avatarLastName || ''}`,
                'small-caps bold 75px Montserrat_regular'
              )
            }
          />
        </div>
        <div className="notification_inner d_flex flex_direction_column">
          <p className="user_project">
            {`${row.title} `} <span>{row.body}</span>
          </p>
          {renderTimestamp(row.createdAt)}
          <div className="unread_notify d_flex_center">
            <CustomImage src={unread_notify} />
          </div>
        </div>
      </>
    );

  const renderRow = (row: NotificationFeedRow) => (
    <li className={`notification_row ${row.isRead ? '' : 'unread'}`} key={row.id}>
      {row.importReport ? (
        // An import row carries two actions, so it cannot be one button the
        // way every other row is: the error chip is a button, and HTML forbids
        // an interactive element inside another. They are siblings instead --
        // a transparent hit area covering the row, with the chip lifted above
        // it.
        //
        // The hit area goes through openRow like every other row, so it marks
        // read, navigates to the list the import filled, and closes the tray.
        // Its own guard covers an import with no destination: those still mark
        // read, they just stay put.
        <div className="notification_main d_flex align_items_center">
          <button type="button" className="notification_read_hit" aria-label={row.title} onClick={() => openRow(row)} />
          {renderRowContent(row)}
        </div>
      ) : (
        <button type="button" className="notification_main d_flex align_items_center" onClick={() => openRow(row)}>
          {renderRowContent(row)}
        </button>
      )}
    </li>
  );

  const renderRetry = (message: string) => (
    <li className="not_available notification_feed_error">
      <p>{message}</p>
      <button type="button" className="btn primary_btn" onClick={handleRetry}>
        Try again
      </button>
    </li>
  );

  const renderBody = () => {
    /**
     * Rows are checked before isError on purpose. A failed load-more leaves the
     * pages that did load in the store, and replacing the whole tray with an
     * error would throw away the list the user was reading. That failure is
     * reported by the footer below instead. Mirrors the web dropdown.
     */
    if (rows.length > 0) {
      return (
        <>
          {rows.map(renderRow)}
          {isLoadingMore && (
            <li className="not_available" role="status">
              Loading more...
            </li>
          )}
          {feedHasError &&
            !isLoadingMore &&
            renderRetry(
              errorIsLoadMore ? "We couldn't load more notifications." : "We couldn't refresh your notifications."
            )}
        </>
      );
    }

    // Also covers the gap before the mount effect runs, when nothing has been
    // requested yet, so the empty copy cannot flash ahead of the first response.
    if (isLoading || !feedHasLoaded) {
      return (
        <li className="not_available" role="status">
          Loading notifications...
        </li>
      );
    }

    if (feedHasError) {
      return renderRetry("We couldn't load your notifications.");
    }

    return (
      <li className="not_available notification_empty">
        <p className="empty_title fw_600 fs_16">Nothing new right now</p>
        <p className="empty_subtitle fw_400 fs_14">We'll let you know when something needs your attention.</p>
      </li>
    );
  };

  /**
   * The header hangs its open/close toggle on the list item this tray renders
   * inside, so without this every click in here bubbles up and shuts the tray.
   * That made "Try again" useless: the retry fired, the tray unmounted, and
   * the unmount bumped the feed requestId, so the response it was waiting for
   * was dropped as stale. Rows are the one thing that should still close it,
   * and they call onClose themselves after navigating.
   */
  const stopPopupToggle = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  return (
    <div className="i_popup_notification" id="notificationRef" onClick={stopPopupToggle}>
      <div className="i_popup_notify_content count_notification_pop">
        <div className="inner_pop_notify">
          <div className="i_popup_header d_flex_between">
            <h4 className="fw_600 fs_20">Notifications</h4>
            {hasUnread && (
              <button type="button" className="fw_400 fs_16 cursor_pointer mark_all_read" onClick={handleMarkAllAsRead}>
                Mark all as read
              </button>
            )}
          </div>
        </div>

        <div className="i_pop_notify_details">
          <div className="all_inner_content" onScroll={handleScroll}>
            <ul className="notification_wrapper">{renderBody()}</ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notification;
